import { clsx } from 'clsx';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ReeferBaseProps } from '../../utils/baseProps.types';
import { Link } from '../link/link';

import styles from './readMore.module.css';

export interface ReadMoreProps extends ReeferBaseProps {
  /** Number of characters to display before truncating, defaults to 250. Ignored when `lines` is set */
  characters?: number;

  /** Text content of the ReadMore component */
  content: string;

  /** Label to show less text, defaults to 'Show less' */
  less?: string;

  /**
   * Number of lines to display before truncating. Clamps with CSS, so the full
   * content is always rendered
   */
  lines?: number;

  /** Label to show all text, defaults to 'Read more' */
  more?: string;
}

/**
 * `ReadMore` component truncates text to a specified number of characters or lines,
 *  giving the option to show more or less text
 */
export const ReadMore = ({
  'data-testid': testId,
  characters = 250,
  className,
  content,
  id,
  less = 'Show less',
  lines,
  more = 'Read more',
  style,
}: ReadMoreProps) => {
  const [expanded, setExpanded] = useState(false);

  const toggleLines = (e: MouseEvent) => {
    e.preventDefault();
    setExpanded(!expanded);
  };

  if (lines) {
    return (
      <LineClampedReadMore
        className={className}
        content={content}
        expanded={expanded}
        id={id}
        less={less}
        lines={lines}
        more={more}
        onToggle={toggleLines}
        style={style}
        testId={testId}
      />
    );
  }

  const isTruncated = content.length > characters;

  return (
    <div className={className} data-testid={testId} id={id} style={style}>
      <span>
        {isTruncated && !expanded ? content.substring(0, characters) : content}
      </span>
      {isTruncated && (
        <span>
          {expanded ? ' ' : '...'}
          <Link onClick={toggleLines}>{!expanded ? more : less}</Link>
        </span>
      )}
    </div>
  );
};

interface LineClampedReadMoreProps extends Pick<
  ReadMoreProps,
  'className' | 'content' | 'id' | 'less' | 'more' | 'style'
> {
  expanded: boolean;
  lines: number;
  onToggle: (e: MouseEvent) => void;
  testId?: string;
}

const LineClampedReadMore = ({
  className,
  content,
  expanded,
  id,
  less,
  lines,
  more,
  onToggle,
  style,
  testId,
}: LineClampedReadMoreProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = contentRef.current;
    if (!element || expanded) return;

    const measure = () =>
      setIsTruncated(element.scrollHeight > element.clientHeight);

    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [content, expanded, lines]);

  return (
    <div className={className} data-testid={testId} id={id} style={style}>
      <div
        ref={contentRef}
        className={clsx({ [styles['clamped']]: !expanded })}
        style={{ '--read-more-lines': lines }}
      >
        {content}
      </div>
      {isTruncated && <Link onClick={onToggle}>{expanded ? less : more}</Link>}
    </div>
  );
};
